// Everything this lib says to the identity server (the identity ADR, "The handshake — a
// person"): the token endpoint, sign-out, and what a person does with a login
// no account owns yet. Every call that the browser's key must stand behind
// carries a fresh proof from it.
//
// Like every module in sdk/, this holds no environment of its own: the host
// supplies where the identity server is and which app this is, the same way it
// supplies every backend URL.
import { createProof, getKeyThumbprint } from './proofKey.js';
import { clearSession, getSession, setSession } from './sessionTokenStorage.js';

// Renew this long before the token runs out, so a request made now does not
// arrive carrying one that expired on the way.
const RENEWAL_SKEW_MS = 30_000;

const identityServer = {
    baseUrl: null,
    appId: null
};

// One renewal at a time: a burst of calls with an expiring token is one refresh.
let renewing = null;

export function configureIdentityServer({ baseUrl, appId } = {}) {
    if (baseUrl !== undefined) identityServer.baseUrl = baseUrl;
    if (appId !== undefined) identityServer.appId = appId;
    return { ...identityServer };
}

function requireConfiguration() {
    if (!identityServer.baseUrl || !identityServer.appId) {
        throw new Error('The identity server is not configured — pass `identityServer` to configureBackends first.');
    }
    return identityServer;
}

function refusal(response, body) {
    const error = new Error(`The identity server answered ${response.status}`);
    error.statusCode = response.status;
    error.body = body;
    return error;
}

async function readJson(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

// The session a call on the person's behalf presents, or a 401 when there is none.
async function requireSession(what) {
    const session = await currentSession();
    if (session === null) {
        const error = new Error(`No session to ${what}.`);
        error.statusCode = 401;
        throw error;
    }
    return session;
}

// One POST to the identity server with a proof for exactly that URL. `headers`
// and `body` say which shape the endpoint reads: a form for /token, JSON elsewhere.
async function post(path, { headers, body, accessToken }) {
    const { baseUrl } = requireConfiguration();
    const proofUrl = `${baseUrl}${path}`;
    const proofHeaders = { DPoP: await createProof({ method: 'POST', url: proofUrl, accessToken }) };
    if (accessToken) {
        proofHeaders.Authorization = `DPoP ${accessToken}`;
    }
    const response = await fetch(proofUrl, {
        method: 'POST',
        headers: { ...headers, ...proofHeaders },
        body
    });
    const answer = await readJson(response);
    if (!response.ok) {
        throw refusal(response, answer);
    }
    return answer;
}

function postForm(path, fields) {
    return post(path, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fields)
    });
}

function postJson(path, payload, { accessToken } = {}) {
    return post(path, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        accessToken
    });
}

/** Where to send the person to sign in. The key's thumbprint and the PKCE challenge ride along, so the code that comes back works for this client only. */
export async function authorizeUrl({ redirectUri, state, codeChallenge, identityProvider, prompt, intent }) {
    const { baseUrl, appId } = requireConfiguration();
    const params = new URLSearchParams({
        client_id: appId,
        response_type: 'code',
        redirect_uri: redirectUri,
        state,
        dpop_jkt: await getKeyThumbprint(),
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });
    if (identityProvider) params.set('identity_provider', identityProvider);
    if (prompt) params.set('prompt', prompt);
    if (intent) params.set('intent', intent);
    return `${baseUrl}/authorize?${params.toString()}`;
}

/** Where to send the person to end the hosted login's own session, returning to `returnTo`. */
export function logoutUrl(returnTo) {
    const { baseUrl, appId } = requireConfiguration();
    const params = new URLSearchParams({ client_id: appId, post_logout_redirect_uri: returnTo });
    return `${baseUrl}/logout?${params.toString()}`;
}

/**
 * The phone's relay: the sign-in sheet caught Cognito's return on the device, and the app hands
 * what Cognito answered to the identity server. The answer is what GET /callback would have sent
 * a browser by redirect: `{ state, code }` with the app's own state, or `{ state, error }`.
 */
export function relayCallback({ code, state, error }) {
    return postJson('/callback', { code, state, error });
}

/**
 * Trades the one-time code. The answer always carries an `access_token`. For a
 * sign-in that found its Human it is a session's, with the refresh token that
 * renews it. For one that did not, the answer says `account_pending`: the token
 * is for the account endpoints only, and nothing renews it.
 */
export function exchangeCode({ code, redirectUri, codeVerifier }) {
    const { appId } = requireConfiguration();
    return postForm('/token', { grant_type: 'authorization_code', code, client_id: appId, redirect_uri: redirectUri, code_verifier: codeVerifier });
}

async function renew(refreshToken) {
    const { appId } = requireConfiguration();
    try {
        const tokens = await postForm('/token', { grant_type: 'refresh_token', refresh_token: refreshToken, client_id: appId });
        return await setSession(tokens);
    } catch (error) {
        // A refused renewal ends the session; an outage does not.
        if (error.statusCode) {
            await clearSession();
            return null;
        }
        throw error;
    }
}

/**
 * The session to present right now: the stored one while its token has life
 * left, a renewed one when it is about to run out, null when there is none or
 * it cannot be renewed any more.
 */
export async function currentSession() {
    const session = await getSession();
    if (session === null) {
        return null;
    }
    if (session.expiresAt - RENEWAL_SKEW_MS > Date.now()) {
        return session;
    }
    if (renewing === null) {
        renewing = renew(session.refreshToken).finally(() => {
            renewing = null;
        });
    }
    return renewing;
}

/**
 * Sign out this device, the server's half: spends this browser's refresh token,
 * forgets its key, and closes its live connections. The person's other devices
 * are untouched.
 */
export async function revokeSession() {
    const session = await getSession();
    if (session === null) {
        return;
    }
    await postJson('/revoke', { refresh_token: session.refreshToken });
}

/**
 * Sign out everywhere, the server's half: ends every session the person has,
 * on every device, this one included. Throws when it could not be done — the
 * person must not believe their other devices are signed out when they are not.
 */
export async function revokeEverywhere() {
    const session = await requireSession('sign out everywhere from');
    await postJson('/revoke/everywhere', {}, { accessToken: session.accessToken });
}

/** Sign out one of the person's other devices, named by the `id` its row in `listSessions` carries. */
export async function revokeDevice(deviceId) {
    const session = await requireSession('sign out a device from');
    await postJson('/revoke/device', { device: deviceId }, { accessToken: session.accessToken });
}

/**
 * The person's sessions, one per device, this one first: `{ id, appId,
 * signInMethod, userAgent, ipAddress, signedInAt, lastActiveAt, isThisDevice }`.
 */
export async function listSessions() {
    const session = await requireSession('list the sessions of');
    return (await getJson('/sessions', { accessToken: session.accessToken })).sessions;
}

// One GET to the identity server, presenting the session's token and a proof for exactly that URL.
async function getJson(path, { accessToken }) {
    const { baseUrl } = requireConfiguration();
    const url = `${baseUrl}${path}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Authorization: `DPoP ${accessToken}`,
            DPoP: await createProof({ method: 'GET', url, accessToken })
        }
    });
    const answer = await readJson(response);
    if (!response.ok) {
        throw refusal(response, answer);
    }
    return answer;
}

/**
 * The person's second factor: `{ available, enabled }`. `available` is false when this sign-in
 * is Google or Microsoft — the provider's own multi-factor covers it. Throws a 409
 * `SIGN_IN_AGAIN` when this device signed in before the identity server kept what it needs, or
 * Cognito no longer honours it: the person signs in again and tries once more.
 */
export async function mfaStatus() {
    const session = await requireSession('read the second factor of');
    return getJson('/accounts/mfa', { accessToken: session.accessToken });
}

/** Starts setting up the authenticator app: `{ secret, otpauthUri }` — what to scan, and the same as text. Nothing is on yet. */
export async function beginMfa() {
    const session = await requireSession('set up a second factor for');
    return postJson('/accounts/mfa/begin', {}, { accessToken: session.accessToken });
}

/** Finishes set-up with the six digits the authenticator app shows. Throws a 400 `MFA_CODE_WRONG` for a wrong code. */
export async function enableMfa(code) {
    const session = await requireSession('set up a second factor for');
    return postJson('/accounts/mfa/enable', { code }, { accessToken: session.accessToken });
}

/** Turns the authenticator app off: `{ enabled: false }`. */
export async function disableMfa() {
    const session = await requireSession('turn off the second factor of');
    return postJson('/accounts/mfa/disable', {}, { accessToken: session.accessToken });
}

// What a person does with a login no account owns yet. The first three present
// the pending-account token the way any request presents a session's — the
// token, and a proof for this request — and each answers with the session that
// replaces it. Attach acts for the person already signed in, on a pending
// login this browser's key holds, named by its id.
export function createAccount(pendingAccessToken) {
    return postJson('/accounts/create', {}, { accessToken: pendingAccessToken });
}

export function restoreAccount(pendingAccessToken) {
    return postJson('/accounts/restore', {}, { accessToken: pendingAccessToken });
}

export function connectMatch(pendingAccessToken) {
    return postJson('/accounts/connect', {}, { accessToken: pendingAccessToken });
}

export async function attachLogin(pendingLoginId) {
    const session = await currentSession();
    if (session === null) {
        const error = new Error('No session to attach a login to.');
        error.statusCode = 401;
        throw error;
    }
    return postJson('/accounts/attach', { pending_login: pendingLoginId }, { accessToken: session.accessToken });
}

export const identityServerClient = {
    authorizeUrl,
    logoutUrl,
    exchangeCode,
    currentSession,
    revokeSession,
    revokeEverywhere,
    revokeDevice,
    listSessions,
    mfaStatus,
    beginMfa,
    enableMfa,
    disableMfa,
    createAccount,
    restoreAccount,
    connectMatch,
    attachLogin
};
export default identityServerClient;
